class HorizontalTabsComponent extends HTMLElement {
  constructor() {
    super();
    this.nav = this.querySelector('.horizontal-tabs__nav');
    this.navScroll = this.querySelector('.horizontal-tabs__nav-scroll');
    this.navWrapper = this.querySelector('.horizontal-tabs__nav-wrapper');
    this.tabButtons = this.querySelectorAll('.horizontal-tabs__tab-button');
    this.panels = this.querySelectorAll('.horizontal-tabs__panel');
    this.prevButton = this.querySelector('.horizontal-tabs__nav-arrow--prev');
    this.nextButton = this.querySelector('.horizontal-tabs__nav-arrow--next');

    if (!this.nav || this.tabButtons.length === 0) return;

    this.init();
  }

  init() {
    
    this.tabButtons.forEach((button, index) => {
      button.addEventListener('click', () => this.activateTab(index));
    });

    
    this.nav.addEventListener('keydown', this.handleKeydown.bind(this));

    
    if (this.prevButton && this.nextButton) {
      this.prevButton.addEventListener('click', () => this.scrollNav('prev'));
      this.nextButton.addEventListener('click', () => this.scrollNav('next'));
    }

    this.checkOverflow();
    this.resizeObserver = new ResizeObserver(() => this.checkOverflow());
    this.resizeObserver.observe(this.nav);

    this.nav.addEventListener('scroll', () => this.updateArrowStates());
  }

  activateTab(index) {
    this.tabButtons.forEach((button, i) => {
      button.classList.remove('horizontal-tabs__tab-button--active');
      button.setAttribute('aria-selected', 'false');
      button.setAttribute('tabindex', '-1');
    });

    this.panels.forEach((panel) => {
      panel.classList.remove('horizontal-tabs__panel--active');
      panel.setAttribute('hidden', '');
    });

    const activeButton = this.tabButtons[index];
    const activePanel = this.panels[index];

    activeButton.classList.add('horizontal-tabs__tab-button--active');
    activeButton.setAttribute('aria-selected', 'true');
    activeButton.setAttribute('tabindex', '0');
    activeButton.focus();

    activePanel.classList.add('horizontal-tabs__panel--active');
    activePanel.removeAttribute('hidden');

    this.scrollTabIntoView(activeButton);
  }

  handleKeydown(event) {
    const currentIndex = Array.from(this.tabButtons).findIndex(
      (button) => button === document.activeElement
    );

    if (currentIndex === -1) return;

    let newIndex;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabButtons.length - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = currentIndex < this.tabButtons.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = this.tabButtons.length - 1;
        break;
      default:
        return;
    }

    this.activateTab(newIndex);
  }

  scrollNav(direction) {
    const scrollAmount = this.nav.clientWidth * 0.75;
    const currentScroll = this.nav.scrollLeft;

    const newScroll =
      direction === 'next'
        ? currentScroll + scrollAmount
        : currentScroll - scrollAmount;

    this.nav.scrollTo({
      left: newScroll,
      behavior: 'smooth',
    });
  }

  scrollTabIntoView(tabButton) {
    const navRect = this.nav.getBoundingClientRect();
    const buttonRect = tabButton.getBoundingClientRect();

    const isFullyVisible =
      buttonRect.left >= navRect.left && buttonRect.right <= navRect.right;

    if (!isFullyVisible) {
      const scrollLeft = tabButton.offsetLeft - this.nav.offsetLeft - 20;
      this.nav.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  }

  checkOverflow() {
    const hasOverflow = this.nav.scrollWidth > this.nav.clientWidth;

    if (hasOverflow) {
      this.navWrapper.classList.remove('horizontal-tabs__nav-wrapper--no-overflow');
      this.nav.classList.remove('horizontal-tabs__nav--centered');
      this.prevButton.classList.remove('hidden');
      this.nextButton.classList.remove('hidden');
    } else {
      this.navWrapper.classList.add('horizontal-tabs__nav-wrapper--no-overflow');
      this.nav.classList.add('horizontal-tabs__nav--centered');
      this.prevButton.classList.add('hidden');
      this.nextButton.classList.add('hidden');
    }

    this.updateArrowStates();
  }

  updateArrowStates() {
    if (!this.prevButton || !this.nextButton) return;

    const scrollLeft = this.nav.scrollLeft;
    const maxScroll = this.nav.scrollWidth - this.nav.clientWidth;

    
    if (scrollLeft <= 0) {
      this.prevButton.setAttribute('disabled', '');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    
    if (scrollLeft >= maxScroll - 1) {
      this.nextButton.setAttribute('disabled', '');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

customElements.define('horizontal-tabs-component', HorizontalTabsComponent);
